import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SearchIcon from "@material-ui/icons/Search";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  paper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  actionCell: {
    width: 140,
  },
}));

const initialForm = {
  name: "",
  code: "",
  description: "",
  isActive: true,
};

const OrganizationSectors = () => {
  const classes = useStyles();
  const history = useHistory();
  const { organizationId } = useParams();

  const [organization, setOrganization] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [deletingSector, setDeletingSector] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadOrganization = async () => {
    try {
      const { data } = await api.get("/business-groups");
      const groups = Array.isArray(data) ? data : [];
      const allOrganizations = groups.flatMap((group) =>
        (group.organizations || []).map((org) => ({
          ...org,
          groupName: group.name,
        }))
      );

      const found = allOrganizations.find((org) => String(org.id) === String(organizationId));
      setOrganization(found || null);
    } catch (err) {
      toastError(err);
    }
  };

  const loadSectors = async () => {
    try {
      const { data } = await api.get(`/organizations/${organizationId}/sectors`);
      setSectors(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    loadOrganization();
    loadSectors();
  }, [organizationId]);

  const openCreateModal = () => {
    setEditingSector(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEditModal = (sector) => {
    setEditingSector(sector);
    setForm({
      name: sector.name || "",
      code: sector.code || "",
      description: sector.description || "",
      isActive: sector.isActive !== false,
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingSector(null);
    setForm(initialForm);
  };

  const handleChangeForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.warn("Nome do setor e obrigatorio.");
      return;
    }

    try {
      setSaving(true);

      if (editingSector) {
        await api.put(`/sectors/${editingSector.id}`, {
          name: form.name.trim(),
          code: form.code.trim() || null,
          description: form.description.trim() || null,
          isActive: form.isActive,
        });
        toast.success("Setor atualizado com sucesso.");
      } else {
        await api.post(`/organizations/${organizationId}/sectors`, {
          name: form.name.trim(),
          code: form.code.trim() || null,
          description: form.description.trim() || null,
          isActive: form.isActive,
        });
        toast.success("Setor criado com sucesso.");
      }

      handleCloseModal();
      await loadSectors();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAskDelete = (sector) => {
    setDeletingSector(sector);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingSector) return;

    try {
      await api.delete(`/sectors/${deletingSector.id}`);
      toast.success("Setor removido com sucesso.");
      setConfirmOpen(false);
      setDeletingSector(null);
      await loadSectors();
    } catch (err) {
      toastError(err);
    }
  };

  const filteredSectors = sectors.filter((sector) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;

    const bucket = [sector.name, sector.code, sector.description]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return bucket.includes(term);
  });

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingSector ? `Excluir setor ${deletingSector.name}?` : "Excluir setor"}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        Esta acao nao pode ser desfeita.
      </ConfirmationModal>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSector ? "Editar Setor" : "Novo Setor"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                value={form.name}
                onChange={(e) => handleChangeForm("name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Codigo"
                value={form.code}
                onChange={(e) => handleChangeForm("code", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Descricao"
                value={form.description}
                onChange={(e) => handleChangeForm("description", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    color="primary"
                    checked={form.isActive}
                    onChange={(e) => handleChangeForm("isActive", e.target.checked)}
                  />
                )}
                label="Setor ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleSave} disabled={saving}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/organizations")}>Voltar</Button>
            <Title>Setores da Organizacao</Title>
            <Typography variant="body2" color="textSecondary">
              {organization ? `${organization.razaoSocial || organization.nomeFantasia} - ${organization.cnpj || ""}` : `Organizacao ${organizationId}`}
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              placeholder="Buscar setor por nome, codigo ou descricao"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
            >
              Novo Setor
            </Button>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Codigo</TableCell>
              <TableCell>Descricao</TableCell>
              <TableCell>Status</TableCell>
              <TableCell className={classes.actionCell} align="right">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSectors.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhum setor cadastrado para esta organizacao.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {filteredSectors.map((sector) => (
              <TableRow key={sector.id}>
                <TableCell>{sector.id}</TableCell>
                <TableCell>{sector.name}</TableCell>
                <TableCell>{sector.code || "-"}</TableCell>
                <TableCell>{sector.description || "-"}</TableCell>
                <TableCell>{sector.isActive ? "Ativo" : "Inativo"}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEditModal(sector)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleAskDelete(sector)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default OrganizationSectors;